/** Typed access to Vite env with trim + clear errors. */

export function getEnv(name: keyof ImportMetaEnv, fallback?: string): string {
  const raw = import.meta.env[name];
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (value) return value;
  if (fallback !== undefined) return fallback;
  throw new Error(`Missing required env var: ${String(name)}`);
}

/** api-devtools `/devtools` mount root; clients append `/v1/...`. */
export function getDevtoolsApiUrl(): string {
  return getEnv('VITE_DEVTOOLS_API_URL').replace(/\/$/, '');
}

/**
 * api-devtools host root (no mount suffix). Derives from `VITE_DEVTOOLS_API_URL`
 * by stripping a trailing `/devtools` face.
 */
export function getDevtoolsHostUrl(): string {
  const mount = getDevtoolsApiUrl();
  if (mount.endsWith('/devtools')) {
    return mount.slice(0, -'/devtools'.length);
  }
  return mount;
}

/**
 * api-devtools `/agents` harness mount root; clients append `/v1/...`.
 * Prefer `VITE_AGENTS_API_URL` when set; otherwise sibling of `/devtools`.
 */
export function getAgentsApiUrl(): string {
  const explicit = (import.meta.env.VITE_AGENTS_API_URL || '').trim();
  if (explicit) return explicit.replace(/\/$/, '');
  return `${getDevtoolsHostUrl()}/agents`;
}

/**
 * api-devtools taskboard mount root (`/taskboard`); clients append `/v1/...`.
 * Prefer `VITE_TASKBOARD_API_URL` when set; otherwise derive sibling mount
 * from `VITE_DEVTOOLS_API_URL` (`…/devtools` → `…/taskboard`).
 */
export function getTaskboardApiUrl(): string {
  const explicit = (import.meta.env.VITE_TASKBOARD_API_URL || '').trim();
  if (explicit) return explicit.replace(/\/$/, '');

  // Same api-devtools host: swap the /devtools face for /taskboard.
  return `${getDevtoolsHostUrl()}/taskboard`;
}

/** Sidebar / document product name from env (not i18n — ops can override per deploy). */
export function getAppTitle(): string {
  return getEnv('VITE_APP_TITLE', 'DevTools');
}

/** GIS OAuth client used by the tenant login page. */
export function getGoogleClientId(): string {
  return getEnv('VITE_GOOGLE_CLIENT_ID');
}

/** True in local Vite; gates DevTokenPanel and other agent-only escape hatches. */
export function isDevelopment(): boolean {
  return import.meta.env.VITE_ENVIRONMENT === 'development';
}
